import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { observer } from "mobx-react-lite";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Text,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  ILocationMenuItem,
  ILocationMenuInsight,
} from "../../../stores/data/operationRuleLocations/OperationRuleLocationModel";
import EmptyState from "../../../components/EmptyState/EmptyState";
import noInsights from "../../../assets/noInsights.svg";
import EventList from "./EventList";
import {
  ChevronUp,
  ChevronRight,
  MoreHorizontal,
  Plus,
  ArrowDownUp,
  Check,
  X,
  GripHorizontal,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Track draggable state per location
interface DraggableStateByLocation {
  [locationId: number]: boolean;
}

// Track pending drag data per location
interface PendingDragDataByLocation {
  [locationId: number]: ILocationMenuInsight[] | null;
}

// Track save key per location (incremented on each save to signal EventList)
interface SaveKeyByLocation {
  [locationId: number]: number;
}

interface LocationAccordionProps {
  dashboardId: number;
  locationMenuList: ILocationMenuItem[];
  disableDragAndDrop?: boolean;
  onEventReorder?: (
    locationId: number,
    newOrder: ILocationMenuInsight[],
  ) => void;
  isLocationDragMode?: boolean;
  locationSaveKey?: number;
  onLocationOrderChange?: (newOrder: ILocationMenuItem[]) => void;
  onDeleteLocation?: (locationId: number, locationName: string) => void;
  onDeleteEvent?: (
    eventId: number,
    eventName: string,
    locationId: number,
  ) => void;
  onEditEvent?: (
    event: ILocationMenuInsight,
    locationId: number,
    locationName: string,
  ) => void;
  onCreateEventForLocation?: (location: ILocationMenuItem) => void;
  onEditLocation?: (location: ILocationMenuItem) => void;
}

const LocationAccordion: React.FC<LocationAccordionProps> = observer(
  ({
    dashboardId,
    locationMenuList,
    disableDragAndDrop = false,
    onEventReorder,
    isLocationDragMode = false,
    locationSaveKey = 0,
    onLocationOrderChange,
    onDeleteLocation,
    onDeleteEvent,
    onEditEvent,
    onCreateEventForLocation,
    onEditLocation,
  }) => {
    const { t } = useTranslation("translation", {
      keyPrefix: "oceanWeatherIntelligence.insightManager.eventManager",
    });

    // Local location order for drag & drop
    const [localLocationOrder, setLocalLocationOrder] =
      useState<ILocationMenuItem[]>(locationMenuList);

    // Track previous save key to detect save vs cancel
    const prevLocationSaveKeyRef = useRef(locationSaveKey);
    const prevIsLocationDragModeRef = useRef(isLocationDragMode);

    // Start with all accordions expanded
    const allIndices = useMemo(
      () => localLocationOrder.map((_, index) => index),
      [localLocationOrder],
    );

    // Track expanded accordion items - start with all expanded
    const [expandedIndices, setExpandedIndices] =
      useState<number[]>(allIndices);

    // Track draggable state per location
    const [draggableByLocation, setDraggableByLocation] =
      useState<DraggableStateByLocation>({});

    // Track pending drag data per location (to save when user clicks save)
    const [pendingDragDataByLocation, setPendingDragDataByLocation] =
      useState<PendingDragDataByLocation>({});

    // Track save key per location (incremented on save to prevent reset in EventList)
    const [saveKeyByLocation, setSaveKeyByLocation] =
      useState<SaveKeyByLocation>({});

    // Sensors for drag & drop
    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    );

    // Update local location order when prop changes (only when not in drag mode)
    useEffect(() => {
      if (!isLocationDragMode) {
        setLocalLocationOrder(locationMenuList);
      }
    }, [locationMenuList, isLocationDragMode]);

    // Handle drag mode changes - reset to prop order when canceling
    useEffect(() => {
      const wasInDragMode = prevIsLocationDragModeRef.current;
      const isNowInDragMode = isLocationDragMode;
      const saveKeyChanged = locationSaveKey !== prevLocationSaveKeyRef.current;

      prevLocationSaveKeyRef.current = locationSaveKey;
      prevIsLocationDragModeRef.current = isLocationDragMode;

      // Reset to prop order when canceling (exiting drag mode WITHOUT save)
      if (wasInDragMode && !isNowInDragMode && !saveKeyChanged) {
        setLocalLocationOrder(locationMenuList);
      }
    }, [isLocationDragMode, locationMenuList, locationSaveKey]);

    // Update expanded indices when localLocationOrder changes
    useEffect(() => {
      setExpandedIndices(localLocationOrder.map((_, index) => index));
    }, [localLocationOrder]);

    // Handle accordion expansion change
    const handleAccordionChange = useCallback((indices: number | number[]) => {
      const newIndices = Array.isArray(indices) ? indices : [indices];
      setExpandedIndices(newIndices);
    }, []);

    // Toggle draggable state for a location
    const toggleDraggable = useCallback(
      (locationId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (disableDragAndDrop) return;

        setDraggableByLocation((prev) => ({
          ...prev,
          [locationId]: !prev[locationId],
        }));
      },
      [disableDragAndDrop],
    );

    // Handle save order for a location
    const handleSaveOrder = useCallback(
      (locationId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const pendingData = pendingDragDataByLocation[locationId];
        if (pendingData && onEventReorder) {
          onEventReorder(locationId, pendingData);
        }

        // Increment saveKey to signal EventList NOT to reset
        setSaveKeyByLocation((prev) => ({
          ...prev,
          [locationId]: (prev[locationId] || 0) + 1,
        }));

        // Exit drag mode
        setDraggableByLocation((prev) => ({
          ...prev,
          [locationId]: false,
        }));

        // Clear pending data
        setPendingDragDataByLocation((prev) => ({
          ...prev,
          [locationId]: null,
        }));
      },
      [pendingDragDataByLocation, onEventReorder],
    );

    // Handle cancel order for a location
    const handleCancelOrder = useCallback(
      (locationId: number, e: React.MouseEvent) => {
        e.stopPropagation();

        // Exit drag mode (EventList will reset to original data)
        setDraggableByLocation((prev) => ({
          ...prev,
          [locationId]: false,
        }));

        // Clear pending data
        setPendingDragDataByLocation((prev) => ({
          ...prev,
          [locationId]: null,
        }));
      },
      [],
    );

    // Handle drag data changes from EventList
    const handleDragDataChange = useCallback(
      (locationId: number, data: ILocationMenuInsight[]) => {
        setPendingDragDataByLocation((prev) => ({
          ...prev,
          [locationId]: data,
        }));
      },
      [],
    );

    // Handle location drag end
    const handleLocationDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = localLocationOrder.findIndex(
          (loc) => loc.id === active.id,
        );
        const newIndex = localLocationOrder.findIndex(
          (loc) => loc.id === over.id,
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = arrayMove(localLocationOrder, oldIndex, newIndex);
          setLocalLocationOrder(newOrder);
          onLocationOrderChange?.(newOrder);
        }
      },
      [localLocationOrder, onLocationOrderChange],
    );

    // Sortable items for locations
    const sortableLocationIds = useMemo(
      () => localLocationOrder.map((loc) => loc.id),
      [localLocationOrder],
    );

    // Render accordion content
    const renderAccordionContent = () => (
      <Accordion
        allowMultiple
        index={expandedIndices}
        onChange={handleAccordionChange}
      >
        {localLocationOrder.map((location, index) => {
          const locationId = location.id;
          const events = location.items;
          const isExpanded = expandedIndices.includes(index);
          const isDraggable = draggableByLocation[locationId] || false;

          return (
            <SortableLocationItem
              key={locationId}
              location={location}
              index={index}
              isExpanded={isExpanded}
              isDraggable={isDraggable}
              isLocationDragMode={isLocationDragMode}
              disableDragAndDrop={disableDragAndDrop}
              events={events}
              saveKeyByLocation={saveKeyByLocation}
              t={t}
              toggleDraggable={toggleDraggable}
              handleSaveOrder={handleSaveOrder}
              handleCancelOrder={handleCancelOrder}
              handleDragDataChange={handleDragDataChange}
              onDeleteLocation={onDeleteLocation}
              onDeleteEvent={onDeleteEvent}
              onEditEvent={onEditEvent}
              onCreateEventForLocation={onCreateEventForLocation}
              onEditLocation={onEditLocation}
            />
          );
        })}
      </Accordion>
    );

    // Wrap with DndContext when in location drag mode
    if (isLocationDragMode) {
      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleLocationDragEnd}
        >
          <SortableContext
            items={sortableLocationIds}
            strategy={verticalListSortingStrategy}
          >
            {renderAccordionContent()}
          </SortableContext>
        </DndContext>
      );
    }

    return renderAccordionContent();
  },
);

// Sortable Location Item Component
interface SortableLocationItemProps {
  location: ILocationMenuItem;
  index: number;
  isExpanded: boolean;
  isDraggable: boolean;
  isLocationDragMode: boolean;
  disableDragAndDrop: boolean;
  events: ILocationMenuInsight[];
  saveKeyByLocation: SaveKeyByLocation;
  t: (key: string) => string;
  toggleDraggable: (locationId: number, e: React.MouseEvent) => void;
  handleSaveOrder: (locationId: number, e: React.MouseEvent) => void;
  handleCancelOrder: (locationId: number, e: React.MouseEvent) => void;
  handleDragDataChange: (
    locationId: number,
    data: ILocationMenuInsight[],
  ) => void;
  onDeleteLocation?: (locationId: number, locationName: string) => void;
  onDeleteEvent?: (
    eventId: number,
    eventName: string,
    locationId: number,
  ) => void;
  onEditEvent?: (
    event: ILocationMenuInsight,
    locationId: number,
    locationName: string,
  ) => void;
  onCreateEventForLocation?: (location: ILocationMenuItem) => void;
  onEditLocation?: (location: ILocationMenuItem) => void;
}

const SortableLocationItem: React.FC<SortableLocationItemProps> = observer(
  ({
    location,
    index,
    isExpanded,
    isDraggable,
    isLocationDragMode,
    disableDragAndDrop,
    events,
    saveKeyByLocation,
    t,
    toggleDraggable,
    handleSaveOrder,
    handleCancelOrder,
    handleDragDataChange,
    onDeleteLocation,
    onDeleteEvent,
    onEditEvent,
    onCreateEventForLocation,
    onEditLocation,
  }) => {
    const locationId = location.id;

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: locationId,
      disabled: !isLocationDragMode,
    });

    const style = isLocationDragMode
      ? {
          transform: CSS.Transform.toString(transform),
          transition,
          touchAction: "none" as const,
        }
      : {};

    const draggingStyle = isDragging
      ? {
          backgroundColor: "#D8E7F0",
          boxShadow: "0 4px 12px rgba(119, 176, 213, 0.3)",
          opacity: 0.9,
          zIndex: 10,
        }
      : {};

    return (
      <AccordionItem
        ref={setNodeRef}
        style={{ ...style, ...draggingStyle }}
        border="none"
        mb={0}
        bg="transparent"
      >
        {/* Location Header */}
        <AccordionButton
          as={Box}
          cursor={isLocationDragMode ? "grab" : "pointer"}
          bg="gray.50"
          py="10px"
          px={4}
          h="auto"
          minH="36px"
          _hover={{ bg: "#f2f8fc" }}
          borderRadius="8px"
          {...(isLocationDragMode ? { ...attributes, ...listeners } : {})}
        >
          <Flex alignItems="center" justifyContent="space-between" w="100%">
            {/* Left Side */}
            <Flex alignItems="center" gap={2}>
              {/* Drag Handle for Location - only in location drag mode */}
              {isLocationDragMode && (
                <Icon
                  as={GripHorizontal}
                  boxSize={4}
                  color="gray.400"
                  opacity={0.5}
                  mr={1}
                />
              )}

              {/* Chevron - hide in location drag mode */}
              {!isLocationDragMode && (
                <Icon
                  as={isExpanded ? ChevronUp : ChevronRight}
                  boxSize={4}
                  color="newBlue.500"
                />
              )}

              {/* Reorder Controls Container - Fixed width to prevent layout shift */}
              {!disableDragAndDrop &&
                !isLocationDragMode &&
                isExpanded &&
                events &&
                events.length >= 1 && (
                  <Box
                    w="36px"
                    h="16px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {/* Reorder Events Button - Only show when not in drag mode */}
                    {!isDraggable && (
                      <Tooltip label={t("reorderEvents")}>
                        <IconButton
                          aria-label={t("reorderEvents")}
                          icon={
                            <Icon
                              as={ArrowDownUp}
                              boxSize={4}
                              color="newBlue.500"
                            />
                          }
                          variant="ghost"
                          size="xs"
                          minW="16px"
                          h="16px"
                          p={0}
                          onClick={(e) => toggleDraggable(locationId, e)}
                          _hover={{ bg: "transparent" }}
                        />
                      </Tooltip>
                    )}

                    {/* Drag Mode Controls - Save and Cancel buttons */}
                    {isDraggable && (
                      <Flex alignItems="center" gap={1}>
                        <Tooltip label={t("saveOrder")}>
                          <IconButton
                            aria-label={t("saveOrder")}
                            icon={<Icon as={Check} boxSize={4} />}
                            size="xs"
                            minW="16px"
                            h="16px"
                            p={0}
                            color="newBlue.500"
                            variant="ghost"
                            _hover={{ bg: "transparent" }}
                            onClick={(e) => handleSaveOrder(locationId, e)}
                          />
                        </Tooltip>
                        <Tooltip label={t("cancelReorder")}>
                          <IconButton
                            aria-label={t("cancelReorder")}
                            icon={<Icon as={X} boxSize={4} />}
                            size="xs"
                            minW="16px"
                            h="16px"
                            p={0}
                            color="gray.500"
                            variant="ghost"
                            _hover={{ bg: "transparent" }}
                            onClick={(e) => handleCancelOrder(locationId, e)}
                          />
                        </Tooltip>
                      </Flex>
                    )}
                  </Box>
                )}

              {/* Vertical Separator - hide in location drag mode */}
              {!isLocationDragMode && (
                <Box w="1px" h="16px" bg="rgba(8, 132, 204, 0.32)" />
              )}

              {/* Location Name */}
              <Text fontSize="14px" fontWeight="600" color="#4a6075">
                {location.name}
              </Text>
            </Flex>

            {/* Right Side - hide in location drag mode */}
            {!isLocationDragMode && (
              <Flex
                alignItems="center"
                gap={4}
                visibility={isDraggable ? "hidden" : "visible"}
              >
                {/* Vertical Divider before menu */}
                <Box w="1px" h="16px" bg="rgba(8, 132, 204, 0.32)" />

                {/* More Options Menu */}
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label={t("options")}
                    icon={
                      <Icon as={MoreHorizontal} boxSize={4} color="gray.700" />
                    }
                    variant="ghost"
                    size="sm"
                    onClick={(e) => e.stopPropagation()}
                    _hover={{ bg: "transparent" }}
                  />
                  <MenuList bg="white" boxShadow="md">
                    <MenuItem
                      color="#555353"
                      bg="white"
                      _hover={{ bg: "gray.100" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditLocation?.(location);
                      }}
                    >
                      {t("editLocation")}
                    </MenuItem>
                    <MenuItem
                      bg="white"
                      _hover={{ bg: "gray.100" }}
                      color="red.500"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteLocation?.(location.id, location.name);
                      }}
                    >
                      {t("deleteLocation")}
                    </MenuItem>
                  </MenuList>
                </Menu>

                {/* New Event Button */}
                {!disableDragAndDrop && (
                  <IconButton
                    aria-label={t("newEvent")}
                    icon={<Plus size={16} color="white" />}
                    size="sm"
                    bg="newBlue.500"
                    borderRadius="6px"
                    _hover={{ bg: "newBlue.600" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateEventForLocation?.(location);
                    }}
                  />
                )}
              </Flex>
            )}
          </Flex>
        </AccordionButton>

        {/* Expanded Panel - hide in location drag mode */}
        {!isLocationDragMode && (
          <AccordionPanel p={0} bg="white">
            {/* Empty State - No Events */}
            {events && events.length === 0 && (
              <Box py={6}>
                <EmptyState description={t("noEvents")} />
              </Box>
            )}

            {/* Event List */}
            {events && events.length > 0 && (
              <EventList
                events={events}
                locationId={locationId}
                locationName={location.name}
                disableDragAndDrop={disableDragAndDrop}
                isDraggable={isDraggable}
                saveKey={saveKeyByLocation[locationId] || 0}
                onDragDataChange={handleDragDataChange}
                onDeleteEvent={onDeleteEvent}
                onEditEvent={onEditEvent}
              />
            )}
          </AccordionPanel>
        )}
      </AccordionItem>
    );
  },
);

export default LocationAccordion;
